import React, { useEffect, useRef, useState, useCallback } from "react";

/**
 * ResponsiveCardFrame — wraps a fixed-size card (650×1000) and scales it down
 * via CSS `transform: scale()` so it always fits inside its parent on small
 * screens, while the underlying DOM node keeps its native 650×1000 dimensions.
 *
 * This means:
 *   • On-screen preview is responsive and never overflows.
 *   • PNG/wallpaper exports via html-to-image still capture the card at its
 *     full native resolution — transforms don't affect the cloned export node.
 *
 * Pass `maxNativeWidth` if your card has a different native width.
 */
const CARD_NATIVE_WIDTH = 650;
const CARD_NATIVE_HEIGHT = 1000;

const ResponsiveCardFrame = ({
  children,
  maxNativeWidth = CARD_NATIVE_WIDTH,
  maxNativeHeight = CARD_NATIVE_HEIGHT,
  align = "center",
  className = "",
  "data-testid": testid,
}) => {
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(1);

  const recompute = useCallback(() => {
    if (!wrapperRef.current) return;
    const w = wrapperRef.current.clientWidth;
    if (!w) return;
    const next = Math.min(1, w / maxNativeWidth);
    setScale(next);
  }, [maxNativeWidth]);

  useEffect(() => {
    recompute();
    const ro = new ResizeObserver(recompute);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    window.addEventListener("resize", recompute);
    window.addEventListener("orientationchange", recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recompute);
      window.removeEventListener("orientationchange", recompute);
    };
  }, [recompute]);

  return (
    <div
      ref={wrapperRef}
      className={className}
      data-testid={testid}
      style={{
        width: "100%",
        maxWidth: maxNativeWidth,
        // Reserve the scaled height so layout below the card doesn't jump.
        height: maxNativeHeight * scale,
        marginLeft: align === "center" ? "auto" : 0,
        marginRight: align === "center" ? "auto" : 0,
      }}
    >
      <div
        style={{
          width: maxNativeWidth,
          height: maxNativeHeight,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default ResponsiveCardFrame;
